/**
 * Remix right surface block control, registered via the UI Control Registry API as a replacement
 * for `RemixButtonDecoration`. Positioning is fully owned by the block-controls right-surface host
 * (`BlockControlsRightSurfaces` / `getSurfacePlacement`) — this component only renders content.
 */
import React, { useCallback, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { BLOCK_CONTROL_UI_CONTEXT } from '@atlaskit/editor-common/block-controls/block-control-ui-context';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { CommonComponentProps } from '@atlaskit/editor-ui-control-model/types';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { TriggerProps } from '@atlaskit/popup/types';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { startRemixCreateExperience } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { getRemixFeatureArea } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-feature-area';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { RemixContainer } from '@atlassian/malleable-ui-components/remix';
import type { RemixOptionSelectionParameters } from '@atlassian/malleable-ui-components/remix/remix-overlay/types';

import type {
	MalleableUiPlugin,
	RemixDisabledReason,
	RemixHiddenOptionsResolver as RemixHiddenOptionsResolverType,
} from '../../malleableUiPluginType';
import { remixHoverHighlightPluginKey } from '../../pm-plugins/remix-hover-highlight-plugin';
import { messages } from '../messages';
import { isRemixInlineDropdownVariant } from '../remix-config';
import { getRemixDisabledTooltip } from '../remix-disabled-tooltip';
import { hasAiEditInProgressState } from '../utils/is-ai-edit-in-progress';
import { EmptyRemixHiddenOptionsResolver } from './RemixHiddenOptionsResolver';

export type RemixRightSurfaceButtonProps = CommonComponentProps & {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	isDisabled?: boolean;
	isRovoLLMEnabled?: boolean;
	livePage?: boolean;
	remixDisabledReason?: RemixDisabledReason;
	remixHiddenOptionsResolver?: RemixHiddenOptionsResolverType;
};

export const RemixRightSurfaceButton = ({
	api,
	isDisabled,
	isRovoLLMEnabled,
	livePage,
	remixDisabledReason,
	surfaceContext,
	remixHiddenOptionsResolver: RemixHiddenOptionsResolver = EmptyRemixHiddenOptionsResolver,
}: RemixRightSurfaceButtonProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const context = surfaceContext?.get(BLOCK_CONTROL_UI_CONTEXT);
	const rootNodeType = context?.rootNode.type.name ?? context?.targetNode.type.name ?? '';
	const rootPos = context?.rootNode.pos ?? context?.targetNode.pos;

	const {
		editorViewMode,
		contentId,
		isOwnRemixing,
		isAnyRemixing,
		aiEditInProgress,
		areAiEntryPointsDisabled,
	} = useSharedPluginStateWithSelector(
		api,
		['editorViewMode', 'contextIdentifier', 'malleableUi', 'aiExperience'],
		(states) => ({
			editorViewMode: states.editorViewModeState?.mode,
			contentId: states.contextIdentifierState?.contextIdentifierProvider?.objectId,
			isOwnRemixing: Boolean(
				states.malleableUiState?.isRemixModalOpen || states.malleableUiState?.isThinking,
			),
			// Only the editor treatment projects this, from the shared Remix registry.
			isAnyRemixing: states.malleableUiState?.isAnyRemixing ?? false,
			areAiEntryPointsDisabled: states.aiExperienceState?.areAiEntryPointsDisabled ?? false,
			aiEditInProgress: hasAiEditInProgressState(states.aiExperienceState),
		}),
	);
	// A Remix from another surface (e.g. the Object Sidebar, including one still generating in
	// Rovo Chat after its handoff) is also in progress, and the machine would refuse a new start.
	const isRemixing = isOwnRemixing || isAnyRemixing;
	const isInlineDropdownVariant = isRemixInlineDropdownVariant();

	// pageMode for analytics: 'editor' = full page editor, 'liveEdit' = live page edit, 'liveView' = live page view (livePage set by Confluence).
	const pageMode =
		editorViewMode === 'edit' && !livePage
			? 'editor'
			: editorViewMode === 'edit' && livePage
				? 'liveEdit'
				: editorViewMode === 'view' && livePage
					? 'liveView'
					: undefined;

	const isRemixAvailable =
		isRovoLLMEnabled && expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true);
	const hasRemixOrEditInProgress =
		(isRemixing || aiEditInProgress) && isExperimentEnabled('cc_maui_polish_changes_batch_6');

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const nodePosRef = useRef<number | undefined>(rootPos);
	nodePosRef.current = rootPos;
	const experienceTracker = useAIMateExperienceTracker();

	const clearHoverHighlight = useCallback(() => {
		if (fg('cc-maui-phase-3')) {
			api?.core.actions.execute(({ tr }) =>
				tr.setMeta(remixHoverHighlightPluginKey, { highlightPos: null }),
			);
		}
	}, [api]);

	const openRemix = useCallback(
		(preselection?: RemixOptionSelectionParameters) => {
			const featureArea = getRemixFeatureArea({});
			experienceTracker.start({
				name: ExperienceName.REMIX_START,
				id: ExperienceName.REMIX_START,
				attributes: {
					entryPoint: 'editor',
					remixSource: 'blockNudge',
					product: 'confluence',
					...(featureArea !== undefined && { featureArea }),
				},
			});
			startRemixCreateExperience({
				experienceTracker,
				attributes: { entryPoint: 'editor', remixSource: 'blockNudge', product: 'confluence' },
			});
			// Clear the hover highlight decoration before opening the modal to prevent flashing
			clearHoverHighlight();
			api?.core.actions.execute(
				api?.malleableUi.commands.openRemixModal({
					nodePos: nodePosRef.current,
					nodeType: rootNodeType,
					metadata: {
						interactionSource: 'remixModalFromNudge',
						...(preselection && {
							preselectedType: preselection.type,
							preselectedSubType: preselection.subType,
							...(preselection.userPrompt && {
								preselectedUserPrompt: preselection.userPrompt,
							}),
						}),
					},
					source: 'blockNudge',
				}),
			);
		},
		[api, clearHoverHighlight, experienceTracker, rootNodeType],
	);

	const handleClick = useCallback(() => {
		if (isDisabled) {
			return;
		}
		expVal('cc-ce-maui-remix-menu-multivariant', 'cohort', 'control');
		expVal('cc-ce-maui-remix-menu-multivariant-aa', 'variant', 'control');

		if (isInlineDropdownVariant) {
			clearHoverHighlight();
			setIsDropdownOpen(true);
			return;
		}
		openRemix();
	}, [clearHoverHighlight, isDisabled, isInlineDropdownVariant, openRemix]);

	const closeDropdown = useCallback(() => setIsDropdownOpen(false), []);

	const handleOptionSelect = useCallback(
		(selection: RemixOptionSelectionParameters) => {
			closeDropdown();
			openRemix(selection);
		},
		[closeDropdown, openRemix],
	);

	const handleRemixButtonMouseEnter = useCallback(() => {
		if (!fg('cc-maui-phase-3') || rootPos === undefined) {
			return;
		}
		api?.core.actions.execute(({ tr }) =>
			tr.setMeta(remixHoverHighlightPluginKey, { highlightPos: rootPos, rootNodeType }),
		);
	}, [api, rootNodeType, rootPos]);

	const handleRemixButtonMouseLeave = useCallback(() => {
		if (!fg('cc-maui-phase-3')) {
			return;
		}
		api?.core.actions.execute(({ tr }) =>
			tr.setMeta(remixHoverHighlightPluginKey, { highlightPos: null }),
		);
	}, [api]);

	if (!isRemixAvailable || areAiEntryPointsDisabled || hasRemixOrEditInProgress) {
		return null;
	}

	const tooltipContent = isRemixing
		? formatMessage(messages.remixInProgressTooltip)
		: isDisabled
			? getRemixDisabledTooltip(formatMessage, remixDisabledReason)
			: fg('cc_maui_updated_remix_tooltip_text')
				? formatMessage(messages.remixBlockTooltipUpdated)
				: formatMessage(messages.remixBlockTooltip);

	const renderIconButton = (triggerProps?: TriggerProps) => (
		<Tooltip content={tooltipContent} position="top">
			<IconButton
				{...triggerProps}
				spacing="compact"
				appearance="subtle"
				shape="circle"
				label={formatMessage(messages.remixBlockLabel)}
				icon={() => <AiGenerativeRemixIcon label="" size="small" />}
				isDisabled={isDisabled || isRemixing}
				isSelected={isInlineDropdownVariant ? isDropdownOpen : undefined}
				analyticsContext={{
					actionSubjectId: 'remix',
					source: 'remixModalFromNudge',
					attributes: {
						...(pageMode !== undefined && { pageMode }),
						nodeType: rootNodeType,
						...(contentId !== undefined && contentId !== '' && { contentId }),
					},
				}}
				testId="visible-block-ctrl-remix-button"
				onMouseEnter={handleRemixButtonMouseEnter}
				onMouseLeave={handleRemixButtonMouseLeave}
				onBlur={handleRemixButtonMouseLeave}
				onMouseDown={(e) => e.preventDefault()}
				onClick={handleClick}
			/>
		</Tooltip>
	);

	return isInlineDropdownVariant ? (
		<RemixHiddenOptionsResolver enabled contentId={contentId}>
			{(hiddenOptions) => (
				<RemixContainer
					hiddenOptions={hiddenOptions}
					presentation="popup"
					trigger={renderIconButton}
					popupPlacement="bottom-end"
					popupOffset={[0, 4]}
					isPopupOpen={isDropdownOpen}
					isThinking={false}
					// Block-controls removes this widget decoration when the editor blurs, which would
					// unmount the menu before an option press is handled.
					shouldPreserveEditorFocus
					onClose={closeDropdown}
					onOptionSelect={handleOptionSelect}
				/>
			)}
		</RemixHiddenOptionsResolver>
	) : (
		renderIconButton()
	);
};
